import { Component, type ErrorInfo, type ReactNode, useMemo } from "react"
import { AggregationType, VISUALIZATIONS_MAP } from "~/ui-rtk/constants/charts"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { Button, DateRangePicker } from "~/ui-rtk/components/ui/controls"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import useMobileView from "~/ui-rtk/hooks/mobile-view"
import useAggregationGroups from "~/ui-rtk/hooks/aggregation-groups"
import MentionsTimeSeriesChart, {
  WIDGET as chartWidgetId,
} from "./components/Chart/MentionsTimeSeriesChart"
import MentionsAnalysisTable, {
  TABLE_WIDGETS,
} from "./components/Table/MentionsAnalysisTable"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import MultiSelectWithChips from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import SentimentByThemeChart from "./components/SentimentByThemeChart/SentimentByThemeChart"
import PostCountByThemeChart from "./components/PostCountByThemeChart/PostCountByThemeChart"
import { useUrlParamsStateArray } from "~/ui-rtk/hooks/urlParamsState"
import { getLightColor, stringToColor } from "~/ui-rtk/utils/chart-utils"
import { type TGraphProps } from "~/ui-rtk/components/ui/charts/ComposedChart/ComposedChart"
import WithLoader from "~/ui-rtk/components/layout/WithLoader/WithLoader"
import { LastUpdatedChip } from "~/ui-rtk/components/ui/common/LastUpdatedChip/LastUpdatedChip"
import { useAnalyzedMentionsCoverage } from "./hooks/analyzed-mentions-coverage"
import {
  NoAnalyzedMentionsState,
  PeriodAfterLatestMentionAlert,
} from "./components/CoverageNotices/CoverageNotices"

type TGraphConfig = TGraphProps & {
  group?: string
}

const dayjs = useDate()

class ChartErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  public state = { hasError: false }

  public static getDerivedStateFromError() {
    return { hasError: true }
  }

  public componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Chart error:", error, info)
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full min-h-32 bg-system-blue-800 rounded-system-md p-system-md">
          <p className="text-basic-white/60">
            Failed to load chart data. Please try refreshing the page.
          </p>
        </div>
      )
    }
    return this.props.children
  }
}

export interface TBrandMentionsAnalysisDashboardProps {
  title: string
  slug: string
  periodPicker?: { visible: boolean }
  parent: {
    slug: string
    title: string
  }
  dateRangeRestrictions?: Partial<{ minDate: Date; maxDate: Date }>
  props?: { commonGranularityPicker?: boolean }
}

const TABLES = {
  THEME: "theme",
  METATHEME: "metatheme",
  BUYING_OCCASION: "buying-occasion",
  CATEGORY: "category",
}

export const WIDGET_IDS = [chartWidgetId, ...Object.values(TABLE_WIDGETS)]

const tables = {
  [TABLES.THEME]: {
    id: TABLES.THEME,
    label: "Themes",
    widget: TABLE_WIDGETS.THEME,
  },
  [TABLES.METATHEME]: {
    id: TABLES.METATHEME,
    label: "Sub-themes",
    widget: TABLE_WIDGETS.METATHEME,
  },
  [TABLES.BUYING_OCCASION]: {
    id: TABLES.BUYING_OCCASION,
    label: "Buying Occasions",
    widget: TABLE_WIDGETS.BUYING_OCCASION,
  },
  [TABLES.CATEGORY]: {
    id: TABLES.CATEGORY,
    label: "Categories",
    widget: TABLE_WIDGETS.CATEGORY,
  },
}

const TAB_URL_PARAM = "tab"
const CHART_SELECTED_METRICS_PARAM = "csm"
const DEFAULT_METRIC_COUNT = 2

export default function BrandMentionsAnalysisDashboard({
  title,
  slug,
  parent,
  dateRangeRestrictions,
  props: contentProps = {},
}: TBrandMentionsAnalysisDashboardProps) {
  const isMobile = useMobileView()
  const { params, setParams } = useURLSearchParams()

  const visualization = VISUALIZATIONS_MAP[chartWidgetId]
  const { graphs = [], groups = [] } = visualization?.props ?? {}

  const typedGraphs = graphs as TGraphConfig[]

  const defaultMetrics = useMemo(
    () => typedGraphs.slice(0, DEFAULT_METRIC_COUNT).map(g => g.dataKey),
    [typedGraphs],
  )

  const graphsWithColors = useMemo(
    () =>
      typedGraphs.map((graph, idx) => ({
        ...graph,
        color: getLightColor(
          stringToColor(`${graph.dataKey}${idx}${graph.label}`),
        ),
      })),
    [typedGraphs],
  )

  const optionsWithGroups = useMemo(
    () =>
      graphsWithColors.map(({ label, group, dataKey, color }) => ({
        label: label ?? dataKey,
        group: group ?? "",
        value: dataKey,
        color: color ?? "",
      })),
    [graphsWithColors],
  )

  const { state: selectedMetricsKeys, setState: setSelectedMetricsKeys } =
    useUrlParamsStateArray<Array<string>>(
      CHART_SELECTED_METRICS_PARAM,
      defaultMetrics,
    )

  const selectedMetrics = useMemo(
    () =>
      optionsWithGroups.filter(({ value }) =>
        (selectedMetricsKeys as string[]).includes(value),
      ),
    [optionsWithGroups, selectedMetricsKeys],
  )

  const activeTable = useMemo(
    () => tables[params[TAB_URL_PARAM] ?? TABLES.THEME],
    [params],
  )

  // The AI analysis can trail the raw mentions feed by months, so the picker's
  // ceiling and the default window follow the latest analyzed mention. Anchored
  // to the company-wide max date instead, a brand whose analysis has stalled
  // opens on a six-month window with nothing in it.
  const coverage = useAnalyzedMentionsCoverage()

  const { dateRange, compareRange } = useMinMaxDateRange(
    dateRangeRestrictions?.minDate,
    dateRangeRestrictions?.maxDate ?? coverage.latestDate,
  )
  const { granularity, setPeriod, setComparePeriod, setGranularity } =
    usePeriod()

  const { commonGranularityPicker } = contentProps

  const aggregationGroups = useAggregationGroups()

  const handleSetWidget = (tab: string) => {
    setParams({
      [TAB_URL_PARAM]: tab.toLowerCase(),
    })
  }

  const handleChangePeriods = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  const handleSetDataAggType = (aggType: AggregationType) => {
    setGranularity(aggType)
  }

  const breadcrumbs = (
    <div className="text-start w-full">
      <Breadcrumbs
        items={[
          {
            slug: parent.slug,
            name: parent.title,
          },
          {
            slug,
            name: title,
          },
        ]}
      />
    </div>
  )

  if (coverage.isLoading || !coverage.hasAnalyzedMentions) {
    return (
      <div>
        {breadcrumbs}
        {coverage.isLoading ? (
          <div className="min-h-124">
            <WithLoader isLoading={true} />
          </div>
        ) : (
          <NoAnalyzedMentionsState />
        )}
      </div>
    )
  }

  const latestAnalyzedDate = coverage.latestDate
    ? dayjs(coverage.latestDate).format(DATE_FORMAT)
    : undefined

  return (
    <div>
      <div
        className={cn({
          "flex items-start justify-between flex-col ": !isMobile,
        })}
      >
        {breadcrumbs}
        <div className="flex justify-between items-start gap-system-sm w-full">
          <div className="w-full">
            <MultiSelectWithChips
              options={optionsWithGroups}
              groups={groups}
              showTypeAhead
              value={selectedMetrics}
              onChange={(options: TMultiSelectOption[]) =>
                setSelectedMetricsKeys(options.map(({ value }) => value))
              }
            />
          </div>
          <div className="flex gap-system-xs justify-end items-stretch">
            <div className="flex items-center shrink-0 whitespace-nowrap">
              <LastUpdatedChip lastDate={latestAnalyzedDate}>
                Analyzed through
              </LastUpdatedChip>
            </div>
            {commonGranularityPicker && (
              <div className="join">
                {aggregationGroups.length > 1
                  ? aggregationGroups.map(({ type, long }) => (
                      <Button
                        variant={{ variant: "solid", color: "blue" }}
                        key={type}
                        title={long}
                        className={cn(
                          `join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md`,
                          { "bg-system-blue-800": type !== granularity },
                          {
                            "bg-system-blue-600": type === granularity,
                          },
                        )}
                        onClick={() => handleSetDataAggType(type)}
                      >
                        {long}
                      </Button>
                    ))
                  : null}
              </div>
            )}
            {dateRange?.[0] &&
              dateRange?.[1] &&
              isValidDate(dateRange[0]) &&
              isValidDate(dateRange[1]) && (
                <DateRangePicker
                  start={dateRange[0]}
                  end={dateRange[1]}
                  granularity={granularity}
                  compareStart={
                    compareRange?.[0] && isValidDate(compareRange[0])
                      ? compareRange[0]
                      : undefined
                  }
                  comparisonEnabled={true}
                  lastUpdatedDate={coverage.latestDate}
                  compareEnd={
                    compareRange?.[1] && isValidDate(compareRange[1])
                      ? compareRange[1]
                      : undefined
                  }
                  onChange={handleChangePeriods}
                />
              )}
          </div>
        </div>
        <PeriodAfterLatestMentionAlert
          dateRange={dateRange}
          latestDate={coverage.latestDate}
        />
        <div className="flex mt-system-md w-full min-h-124">
          <div className="grow-0 shrink-0 min-h-124 basis-2/3 pe-system-xs">
            <ChartErrorBoundary>
              <MentionsTimeSeriesChart
                selectedMetrics={selectedMetrics}
                granularity={granularity}
                dateRange={dateRange}
                compareRange={compareRange ?? undefined}
                graphs={graphsWithColors}
              />
            </ChartErrorBoundary>
          </div>
          <div className="basis-1/3 grow-0 shrink-0 h-full ps-system-xs">
            <div className="flex flex-col justify-between w-full h-full">
              <div className="w-full h-1/2 pb-system-xs">
                <ChartErrorBoundary>
                  <SentimentByThemeChart
                    dateRange={dateRange}
                    compareRange={compareRange ?? undefined}
                  />
                </ChartErrorBoundary>
              </div>
              <div className="w-full h-1/2 pt-system-xs">
                <ChartErrorBoundary>
                  <PostCountByThemeChart
                    dateRange={dateRange}
                    compareRange={compareRange ?? undefined}
                  />
                </ChartErrorBoundary>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="my-system-lg">
        <TabsGroup
          options={Object.values(tables).map(tab => ({
            label: tab.label,
            id: tab.id,
          }))}
          onChange={value => handleSetWidget(Object.values(tables)[value].id)}
          index={Object.values(tables).findIndex(
            tab => tab.id === activeTable.id,
          )}
        >
          {tab => (
            <div className="flex items-center gap-system-xs">
              <span>{tab.label}</span>
            </div>
          )}
        </TabsGroup>
      </div>
      <ChartErrorBoundary>
        <MentionsAnalysisTable
          widget={activeTable.widget}
          granularity={granularity}
          dateRange={dateRange}
          compareRange={compareRange ?? undefined}
        />
      </ChartErrorBoundary>
    </div>
  )
}
